import { Center, Loader } from "@mantine/core";
import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";

import { useServerLangGate } from "@/lib/i18n/useServerLang";
import { useMe } from "@/lib/queries/useMe";
import { useUserStore } from "@/store/user-store";

/**
 * 인증 가드 - 미인증 사용자를 로그인 페이지로 리다이렉트
 *
 * SPA이므로 클라이언트 측 가드 역할. 실제 인증 검증은 백엔드 API(prod=Keycloak 토큰,
 * dev=Bearer 식별 토큰). 이 가드는 UX 보조 — 신뢰 경계가 아님.
 *
 * 세션 복원은 App 부트스트랩(getUser→setUser+setHydrated)이 수행함.
 * 복원 완료(isHydrated) 전에는 로더를 표시해 빈 화면을 방지함.
 *
 * dev 도 실제 폼 로그인(이메일+비밀번호)을 거치므로 prod 와 동일한 가드를 적용함
 * (이전의 dev 무조건 통과 우회 제거). 미로그인 시 /login 으로 보냄.
 *
 * 로그인 사용자는 /auth/me 의 서버 언어(users.locale)가 적용될 때까지 로더를 보임(useServerLangGate) —
 * 브라우저 언어로 먼저 그리면 메모된 번역 라벨이 그 언어로 남음.
 */
interface AuthGuardProps {
  children: ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const user = useUserStore((s) => s.user);
  const isHydrated = useUserStore((s) => s.isHydrated);
  const location = useLocation();
  const me = useMe(Boolean(user));
  const langReady = useServerLangGate(!me.isPending, me.data?.locale);

  if (!isHydrated) {
    // 세션 복원 중 — 로더 표시(빈 화면 방지)
    return (
      <Center mih="100vh">
        <Loader />
      </Center>
    );
  }

  if (!user) {
    // 미인증 -> 로그인 페이지로 (현재 경로를 returnUrl로 전달)
    return <Navigate to="/login" state={{ returnUrl: location.pathname }} replace />;
  }

  if (!langReady) {
    // 서버 언어 적용 대기(/auth/me 조회 중 또는 언어 전환 직전)
    return (
      <Center mih="100vh">
        <Loader />
      </Center>
    );
  }

  return <>{children}</>;
}
